import { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useActivityFilters } from '@/hooks/use-activity-filters'
import { useChildStore } from '@/lib/store/child'
import { ChildSelector, ChildDropdownItem } from '@/components/dashboard/child-selector/ChildSelector'
import { ActivityData } from '@/components/activities/ActivityData'
import { ActivityTables } from '@/components/activities/tables/ActivityTables'
import { ActivityFilters } from '@/components/activities/ActivityFilters'
import { ActivityType } from "@/types/activity"
import { deleteActivity, updateActivity } from "@/lib/services/activity-service"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Loader2, Plus } from "lucide-react"
import { useToast } from "@/components/ui/use-toast"
import { useQueryClient } from "@tanstack/react-query"
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { ActivityEditDialog } from "@/components/activities/ActivityEditDialog"
import { useActivityTables } from "@/hooks/use-activity-tables"

/**
 * Streamlined Activities page component
 * Focuses on displaying three simple tables with minimal UI
 */
export function Activities() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [isDeleting, setIsDeleting] = useState(false)
  const [activityToDelete, setActivityToDelete] = useState<{activity: ActivityType, type: 'sleep' | 'nap' | 'feeding'} | null>(null)
  const [editingActivity, setEditingActivity] = useState<{activity: ActivityType, type: 'sleep' | 'nap' | 'feeding'} | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  
  const {
    tabValue,
    timeFilter,
    searchQuery,
    timeRange,
    setTabValue,
    setTimeFilter,
    setSearchQuery
  } = useActivityFilters()
  
  const { selectedChild, setSelectedChild } = useChildStore()

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, timeFilter, tabValue, selectedChild?.id]);

  // Convert TimeRange dates to strings for the API
  const apiTimeRange = useMemo(() => {
    if (!timeRange) return null;
    return {
      start: timeRange.start.toISOString(),
      end: timeRange.end.toISOString()
    };
  }, [timeRange]);

  // Handle activities data from API
  const { 
    sleepActivities, 
    napActivities, 
    feedingActivities,
    isLoading,
    error
  } = useActivityTables(selectedChild?.id, {
    timeRange: apiTimeRange,
    searchQuery,
    useGlobalChildStore: true
  });

  // Filter and paginate activities
  const filteredSleepActivities = useMemo(() => {
    return filterActivitiesBySearch(sleepActivities, searchQuery);
  }, [sleepActivities, searchQuery]);

  const filteredNapActivities = useMemo(() => {
    return filterActivitiesBySearch(napActivities, searchQuery);
  }, [napActivities, searchQuery]);

  const filteredFeedingActivities = useMemo(() => {
    return filterActivitiesBySearch(feedingActivities, searchQuery);
  }, [feedingActivities, searchQuery]);

  // Paginate activities
  const paginatedSleepActivities = useMemo(() => {
    return paginateActivities(filteredSleepActivities, currentPage, pageSize);
  }, [filteredSleepActivities, currentPage, pageSize]);

  const paginatedNapActivities = useMemo(() => {
    return paginateActivities(filteredNapActivities, currentPage, pageSize);
  }, [filteredNapActivities, currentPage, pageSize]);

  const paginatedFeedingActivities = useMemo(() => {
    return paginateActivities(filteredFeedingActivities, currentPage, pageSize);
  }, [filteredFeedingActivities, currentPage, pageSize]);

  // Helper functions for filtering and pagination
  function filterActivitiesBySearch(activities: ActivityType[], query: string): ActivityType[] {
    if (!query.trim()) return activities;
    
    const searchLower = query.toLowerCase();
    return activities.filter(activity => {
      // Search in various activity fields (customize based on your needs)
      return (
        (activity.notes && activity.notes.toLowerCase().includes(searchLower)) ||
        (activity.startTime && activity.startTime.toLowerCase().includes(searchLower)) ||
        (activity.endTime && activity.endTime.toLowerCase().includes(searchLower)) ||
        (activity.type && activity.type.toLowerCase().includes(searchLower)) ||
        // Feeding specific fields
        (activity.feeding_type && activity.feeding_type.toLowerCase().includes(searchLower)) ||
        // Sleep specific fields
        (activity.location && activity.location.toLowerCase().includes(searchLower))
      );
    });
  }

  function paginateActivities(activities: ActivityType[], page: number, size: number): ActivityType[] {
    const startIndex = (page - 1) * size;
    return activities.slice(startIndex, startIndex + size);
  }

  // Handler for child selection
  const handleChildSelect = (child: ChildDropdownItem) => {
    // Convert the ChildDropdownItem to a Child object for the store
    const selectedChild = {
      id: child.id,
      name: child.name,
      date_of_birth: child.date_of_birth || '',
    };
    
    // Update the global selected child
    setSelectedChild(selectedChild);
    setCurrentPage(1); // Reset to first page on child change
  };

  // Activity editing handlers
  const handleEditActivity = (activity: ActivityType, type: 'sleep' | 'nap' | 'feeding') => {
    // Log activity details before opening the edit dialog
    console.log(`Editing ${type} activity:`, activity);
    
    // For sleep sessions, format the data properly before editing
    if (type === 'sleep') {
      // Clone the activity to avoid reference issues
      const sleepActivity = { ...activity };
      
      // Ensure proper field mappings from DB to UI fields
      if (sleepActivity.location === null) sleepActivity.location = '';
      if (sleepActivity.environment === null) sleepActivity.environment = '';
      if (sleepActivity.onset_method === null) sleepActivity.onset_method = '';
      if (sleepActivity.quality === null) sleepActivity.quality = '';
      if (sleepActivity.sound === null) sleepActivity.sound = '';
      if (sleepActivity.sound_level === null) sleepActivity.sound_level = '';
      
      setEditingActivity({ activity: sleepActivity, type });
    } else {
      setEditingActivity({ activity, type });
    }
  }

  const handleSuccessfulEdit = () => {
    // Show toast notification
    toast({
      title: "Success",
      description: "Activity updated successfully",
      variant: "default"
    });
    
    // Refresh data queries
    queryClient.invalidateQueries({
      queryKey: ['sleepActivities', selectedChild?.id],
    });
    queryClient.invalidateQueries({
      queryKey: ['napActivities', selectedChild?.id],
    });
    queryClient.invalidateQueries({
      queryKey: ['feedingActivities', selectedChild?.id],
    });
    
    // Reset current page to 1 to avoid pagination issues
    setCurrentPage(1);
    
    // Clear editing state
    setEditingActivity(null);
  }

  // Activity delete handlers
  const handleDeleteActivity = (activity: ActivityType, type: 'sleep' | 'nap' | 'feeding') => {
    setActivityToDelete({ activity, type })
  }

  const confirmDeleteActivity = async () => {
    if (!activityToDelete) return
    
    setIsDeleting(true)
    try {
      await deleteActivity(activityToDelete.activity.id, activityToDelete.type)
      
      // Refresh queries
      queryClient.invalidateQueries({
        queryKey: ['sleepActivities', selectedChild?.id],
      })
      queryClient.invalidateQueries({
        queryKey: ['napActivities', selectedChild?.id],
      })
      queryClient.invalidateQueries({
        queryKey: ['feedingActivities', selectedChild?.id],
      })
      
      toast({
        title: "Activity deleted",
        description: "The activity has been successfully deleted.",
      })
    } catch (error) {
      console.error("Error deleting activity:", error)
      toast({
        title: "Error",
        description: "Failed to delete the activity. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsDeleting(false)
      setActivityToDelete(null)
    }
  }

  // Handle page change from pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Get user's timezone (or default to browser timezone)
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Back navigation */}
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back
      </Button>
      
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold tracking-tight">Activities</h1>
        
        {/* Child selector */}
        <ChildSelector 
          selectedChild={selectedChild || null}
          onChildSelect={handleChildSelect}
          className="w-full md:w-auto"
        />
      </div>

      {/* Activity filters */}
      <ActivityFilters
        timeFilter={timeFilter}
        searchQuery={searchQuery}
        onTimeFilterChange={setTimeFilter}
        onSearchChange={setSearchQuery}
      />
            
      {/* Activity tables */}
      <ActivityTables 
        sleepActivities={paginatedSleepActivities} 
        napActivities={paginatedNapActivities}
        feedingActivities={paginatedFeedingActivities}
        isLoading={isLoading}
        error={error}
        tabValue={tabValue}
        onTabChange={setTabValue}
        onEditActivity={handleEditActivity}
        onDeleteActivity={handleDeleteActivity}
        timezone={userTimezone}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!activityToDelete} onOpenChange={(open) => !open && setActivityToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this activity record.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteActivity}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Edit dialog with proper prop mapping */}
      {editingActivity && (
        <ActivityEditDialog
          activity={editingActivity.activity}
          activityType={editingActivity.type}
          isOpen={!!editingActivity}
          onClose={handleSuccessfulEdit}
        />
      )}
    </div>
  )
}
